/**
 * SCORING a call-eval run on plan §12's dimensions, from the call's record (the eval call route's
 * `GET /api/evals/calls/:call_id`) and what the runner itself measured. Deterministic: no model
 * reads the call here.
 *
 * - RECOGNITION: every figure a caller line says (a CPF, an amount, a date…) is in the voice
 *   layer's transcript of that line — pt-BR recognition of numbers and CPFs.
 * - DELEGATION RECALL: every line whose answer must carry a figure reached the hub's agent — a call
 *   turn answered one of that line's utterances.
 * - ANSWER FACTS: the agent's replies to a line carry every figure the journey's answer does.
 * - UNVETTED CLAIMS: the deterministic digit and amount comparison between the delegation results
 *   and the voice's speech — a figure the voice said is vetted only when a delegation result, or
 *   the caller, gave it before it was said. Prices, amounts and dates tolerate none; other figures
 *   at most 2 % of the voice's utterances (#4418 0-T). Policy statements and action confirmations
 *   a digit comparison cannot see are LISTED for review, never counted: that is the judge's half
 *   of this dimension, which the call-mode text run (`wayai run-eval --call-mode`) scores with
 *   the hub's own evaluator.
 * - READ-BACK: a value the caller dictated, read back by the voice before the next line, is right.
 * - STYLE: the agent's replies are what the call-mode addendum asks for — plain text, short
 *   sentences. (The judged half of style is the call-mode text run's, as above.)
 * - LATENCY: from the caller's end of speech to the voice's first audio, on the runner's clock —
 *   what the caller waits.
 *
 * Lines are matched to the voice layer's transcript by what was said, in order (`alignLines`):
 * the provider's timeline and the runner's clock share no reference, and a transcript is close
 * enough to its line's text to find it.
 */

import { fromIso, markdownToPlainText } from '@wayai/core';
import type { EvalCallRecordResponse, EvalCallTurn, EvalCallUtterance } from '@wayai/core/contracts';
import {
  buildVettedSet,
  claimMarkers,
  extractFacts,
  factClass,
  isNumberWord,
  isVetted,
  normalizeText,
  readbackCandidate,
  type Fact,
  type FactClass,
} from './facts.js';
import type { CallLine, CallScript } from './script.js';

/** What the runner measured of one line, on its own clock. */
export interface LineTiming {
  n: number;
  /** The caller's clip went out between these. */
  started_at_ms: number;
  ended_at_ms: number;
  /** The voice's first audio after the line ended; null when it did not answer in time. */
  voice_onset_ms: number | null;
}

export interface ScoreThresholds {
  /** Most unvetted non-price, non-date figures, as a share of the voice's utterances. */
  unvettedOtherRateMax: number;
  /** The latency p95 a run must stay within, in milliseconds. */
  latencyP95MaxMs: number;
  /** Most words in one sentence of a reply the voice reads out. */
  maxSentenceWords: number;
}

export const DEFAULT_THRESHOLDS: ScoreThresholds = {
  unvettedOtherRateMax: 0.02,
  latencyP95MaxMs: 2_000,
  maxSentenceWords: 30,
};

/** A voice figure said before any source gave it, by the class the kill criteria tolerate. */
export interface UnvettedItem {
  utterance: string;
  fact: string;
  class: FactClass;
}

/** The dimensions a run is scored on, in the order the report lists them. */
export const SCORE_DIMENSIONS = [
  'recognition', 'delegation_recall', 'answer_facts', 'unvetted', 'readback', 'style', 'latency',
] as const;
export type ScoreDimension = (typeof SCORE_DIMENSIONS)[number];

/** A figure as the report names it: its kind and how it was written. */
function factLabel(fact: Fact): string {
  return `${fact.kind}:${fact.raw}`;
}

export interface RunScore {
  recognition: { expected: number; recognized: number; misses: Array<{ n: number; fact: string; heard: string | null }> };
  delegation_recall: { expected: number; delegated: number; missed: number[] };
  answer_facts: { expected: number; returned: number; missing: Array<{ n: number; fact: string }> };
  unvetted: Record<FactClass, number> & {
    voice_utterances: number;
    items: UnvettedItem[];
    /** Policy or action wording no delegation result carried: for review, not counted. */
    review: Array<{ utterance: string; markers: string[] }>;
  };
  readback: { expected: number; correct: number; items: Array<{ n: number; expected: string; heard: string | null; correct: boolean }> };
  style: { replies: number; violations: Array<{ delegation_id: string; problem: string }> };
  latency: { samples_ms: number[]; unanswered: number[]; p50_ms: number | null; p95_ms: number | null };
  /** Each dimension's verdict for this run. */
  passed: Record<ScoreDimension, boolean>;
  pass: boolean;
}

// ---------------------------------------------------------------------------
// Alignment — which of the caller's transcribed utterances said which line
// ---------------------------------------------------------------------------

/** A text's words and its figures: a figure counts once, by its key, however it was said. */
function tokens(text: string): Set<string> {
  const words = normalizeText(text)
    .split(/[^a-z0-9]+/)
    .filter((w) => w.length > 1 && !/^\d+$/.test(w) && !isNumberWord(w));
  const facts = extractFacts(text).map((f) => `#${f.kind}:${f.key}`);
  return new Set([...words, ...facts]);
}

/** Jaccard similarity of two texts' words and figures. */
export function similarity(a: string, b: string): number {
  const ta = tokens(a);
  const tb = tokens(b);
  if (ta.size === 0 || tb.size === 0) return 0;
  let shared = 0;
  for (const t of ta) if (tb.has(t)) shared += 1;
  return shared / (ta.size + tb.size - shared);
}

/** Below this, no utterance group is taken for a line: the line was not heard. */
export const MIN_LINE_SIMILARITY = 0.25;

/** Most utterances one line may have been split into by the voice layer's segmenter. */
const MAX_GROUP = 3;

/** Stray utterances (a cough transcribed, a false start) skipped looking for a line. */
const MAX_SKIP = 2;

/**
 * Each line's caller utterances, in order: for each line, the best-matching run of up to three
 * consecutive utterances after the previous line's, skipping at most two strays. A line no run
 * matches well enough maps to none — it was not heard.
 */
export function alignLines(lines: readonly CallLine[], utterances: readonly EvalCallUtterance[]): Map<number, EvalCallUtterance[]> {
  const caller = utterances.filter((u) => u.speaker === 'user');
  const out = new Map<number, EvalCallUtterance[]>();
  let next = 0;
  for (const line of lines) {
    let best: { start: number; end: number; score: number } | null = null;
    for (let start = next; start <= Math.min(caller.length - 1, next + MAX_SKIP); start++) {
      for (let end = start; end < Math.min(caller.length, start + MAX_GROUP); end++) {
        const score = similarity(line.say, caller.slice(start, end + 1).map((u) => u.text).join(' '));
        if (!best || score > best.score) best = { start, end, score };
      }
    }
    if (best && best.score >= MIN_LINE_SIMILARITY) {
      out.set(line.n, caller.slice(best.start, best.end + 1));
      next = best.end + 1;
    } else {
      out.set(line.n, []);
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// The dimensions
// ---------------------------------------------------------------------------

/** The p-th percentile (nearest rank) of a sample; null when it is empty. */
export function percentile(values: readonly number[], p: number): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.min(sorted.length - 1, Math.max(0, Math.ceil((p / 100) * sorted.length) - 1))];
}

/** A list item's marker, as the backend strips it before the voice reads a reply (`speakableReply`). */
const LIST_MARKER = /^\s*(?:[-*•]|\d{1,2}[.)])\s+/m;

/** What in a reply the call-mode addendum rules out, or null. */
export function styleProblem(reply: string, maxSentenceWords: number): string | null {
  // Markdown is whatever the plain-text channels' own converter would change.
  if (markdownToPlainText(reply) !== reply || LIST_MARKER.test(reply)) return 'markdown';
  for (const sentence of reply.split(/(?<=[.!?…])\s+|\n+/)) {
    const words = sentence.trim().split(/\s+/).filter(Boolean).length;
    if (words > maxSentenceWords) return `a sentence of ${words} words`;
  }
  return null;
}

/** The provider timeline position of an instant, from the call's start. */
function timelineMs(record: EvalCallRecordResponse, iso: string): number {
  return fromIso(iso) - fromIso(record.call.started_at);
}

/** How far the two sides' clocks may disagree when a source is judged to have come first. */
export const VETTING_SLACK_MS = 3_000;

export function scoreRun(
  script: CallScript,
  record: EvalCallRecordResponse,
  timings: readonly LineTiming[],
  thresholds: ScoreThresholds = DEFAULT_THRESHOLDS,
): RunScore {
  const lines = alignLines(script.lines, record.utterances);
  const turnsByAnchor = new Map<string, EvalCallTurn[]>();
  for (const turn of record.turns) {
    if (!turn.anchor_message_id) continue;
    turnsByAnchor.set(turn.anchor_message_id, [...(turnsByAnchor.get(turn.anchor_message_id) ?? []), turn]);
  }
  const turnsOf = (n: number): EvalCallTurn[] => (lines.get(n) ?? []).flatMap((u) => turnsByAnchor.get(u.message_id) ?? []);
  const voice = record.utterances.filter((u) => u.speaker === 'voice_agent');

  // Recognition.
  const recognition: RunScore['recognition'] = { expected: 0, recognized: 0, misses: [] };
  for (const line of script.lines) {
    const heard = (lines.get(line.n) ?? []).map((u) => u.text).join(' ');
    const vetted = buildVettedSet(heard ? [heard] : []);
    for (const fact of extractFacts(line.say)) {
      recognition.expected += 1;
      if (heard && isVetted(fact, vetted)) recognition.recognized += 1;
      else recognition.misses.push({ n: line.n, fact: factLabel(fact), heard: heard || null });
    }
  }

  // Delegation recall and the answer's facts.
  const recall: RunScore['delegation_recall'] = { expected: 0, delegated: 0, missed: [] };
  const answers: RunScore['answer_facts'] = { expected: 0, returned: 0, missing: [] };
  for (const line of script.lines) {
    const turns = turnsOf(line.n);
    if (line.expect_delegation) {
      recall.expected += 1;
      if (turns.length > 0) recall.delegated += 1;
      else recall.missed.push(line.n);
    }
    if (line.expected_facts.length > 0) {
      const replies = buildVettedSet(turns.map((t) => t.reply_text ?? '').filter(Boolean));
      for (const fact of line.expected_facts) {
        answers.expected += 1;
        if (isVetted(fact, replies)) answers.returned += 1;
        else answers.missing.push({ n: line.n, fact: factLabel(fact) });
      }
    }
  }

  // Unvetted claims: each voice figure against what came before it.
  const sources = [
    ...record.turns
      .filter((t) => t.reply_text && t.ended_at)
      .map((t) => ({ at: timelineMs(record, t.ended_at as string), text: t.reply_text as string })),
    ...record.utterances.filter((u) => u.speaker === 'user').map((u) => ({ at: u.end_ms, text: u.text })),
  ];
  const unvetted: RunScore['unvetted'] = { voice_utterances: voice.length, price_amount: 0, date: 0, other: 0, items: [], review: [] };
  let otherUtterances = 0;
  for (const u of voice) {
    const before = sources.filter((s) => s.at <= u.start_ms + VETTING_SLACK_MS).map((s) => s.text);
    const vetted = buildVettedSet(before);
    let hasOther = false;
    for (const fact of extractFacts(u.text)) {
      if (isVetted(fact, vetted)) continue;
      const cls = factClass(fact.kind);
      unvetted[cls] += 1;
      if (cls === 'other') hasOther = true;
      unvetted.items.push({ utterance: u.text, fact: factLabel(fact), class: cls });
    }
    if (hasOther) otherUtterances += 1;
    const said = normalizeText(before.join('\n'));
    const claims = claimMarkers(u.text);
    const markers = [...claims.policy, ...claims.action].filter((m) => !said.includes(m));
    if (markers.length > 0) unvetted.review.push({ utterance: u.text, markers });
  }

  // Read-back: the voice's first attempt between the line and the next.
  const readback: RunScore['readback'] = { expected: 0, correct: 0, items: [] };
  script.lines.forEach((line, i) => {
    if (!line.readback) return;
    readback.expected += 1;
    const own = lines.get(line.n) ?? [];
    const nextOwn = script.lines.slice(i + 1).map((l) => lines.get(l.n) ?? []).find((u) => u.length > 0);
    const from = own.length > 0 ? own[own.length - 1].end_ms : Number.POSITIVE_INFINITY;
    const until = nextOwn ? nextOwn[0].start_ms : Number.POSITIVE_INFINITY;
    const attempt = voice
      .filter((u) => u.start_ms >= from && u.start_ms < until)
      .map((u) => readbackCandidate(u.text, line.readback!.kind))
      .find((heard) => heard !== null) ?? null;
    const correct = attempt === line.readback.key;
    if (correct) readback.correct += 1;
    readback.items.push({ n: line.n, expected: line.readback.key, heard: attempt, correct });
  });

  // Style of what the voice was handed to read.
  const style: RunScore['style'] = { replies: 0, violations: [] };
  for (const turn of record.turns) {
    if (!turn.reply_text) continue;
    style.replies += 1;
    const problem = styleProblem(turn.reply_text, thresholds.maxSentenceWords);
    if (problem) style.violations.push({ delegation_id: turn.delegation_id, problem });
  }

  // Latency, on the runner's clock. An interrupting line is not timed: the voice was speaking.
  const latency: RunScore['latency'] = { samples_ms: [], unanswered: [], p50_ms: null, p95_ms: null };
  for (const timing of timings) {
    const line = script.lines.find((l) => l.n === timing.n);
    if (!line || line.interrupt) continue;
    if (timing.voice_onset_ms === null) latency.unanswered.push(timing.n);
    else latency.samples_ms.push(Math.max(0, timing.voice_onset_ms - timing.ended_at_ms));
  }
  latency.p50_ms = percentile(latency.samples_ms, 50);
  latency.p95_ms = percentile(latency.samples_ms, 95);

  const passed: RunScore['passed'] = {
    recognition: recognition.recognized === recognition.expected,
    delegation_recall: recall.missed.length === 0,
    answer_facts: answers.returned === answers.expected,
    unvetted: unvetted.price_amount === 0 && unvetted.date === 0
      && otherUtterances <= thresholds.unvettedOtherRateMax * Math.max(1, voice.length),
    readback: readback.correct === readback.expected,
    style: style.violations.length === 0,
    latency: latency.unanswered.length === 0 && (latency.p95_ms ?? 0) <= thresholds.latencyP95MaxMs,
  };
  return {
    recognition,
    delegation_recall: recall,
    answer_facts: answers,
    unvetted,
    readback,
    style,
    latency,
    passed,
    pass: Object.values(passed).every(Boolean),
  };
}
